/**
 * MONSTER TOWN — THE PICTURES AN ERRAND NEEDS. ERRAND CREW (src/ui/errands*).
 *
 * LAW #4 makes this file load-bearing rather than decorative. An errand step is
 * shown ONLY as a picture — `{ kind: 'gather', item: 'ing_fruit', icon: 'fruit' }`
 * puts the word "fruit" nowhere on screen, it puts a drawing of fruit there —
 * so every icon key used in `src/content/errands.ts` has to resolve to
 * something a three-year-old can name out loud.
 *
 * `src/ui/icons.ts` is the HUD crew's set and this crew does not edit it. The
 * five drawings below are the ones the expansion's data needs and the base set
 * does not have (`talk`, `fruit`, `bowl`, `zoo`, `bench`) plus the two this
 * layer needs for itself (`note`, `guide`). Everything else — `plant`, `tree`,
 * `fence`, `roof`, `light`, `path`, `water`, `townProp`, `decor` — already
 * exists there and is used unchanged, which is why a neighbour request for any
 * build category is a data drop with no art work at all.
 *
 * SAME HOUSE STYLE, DELIBERATELY: one viewBox (0 0 100 100), one ink colour,
 * stroke 7 for silhouette and 5 for detail, round caps, fills from
 * `palette.ts`, zero external assets. Held to the same test: legible at 44 px
 * on a 1024x768 iPad, not at 200 px in a design tool.
 */

import { css, type PaletteName } from '../core/palette'
import { hasIcon, iconEl, iconSvg } from './icons'

const INK = css('ink')
const S = `stroke="${INK}" stroke-linecap="round" stroke-linejoin="round"`
const OUT = `${S} stroke-width="7"`
const DET = `${S} stroke-width="5"`

function f(name: PaletteName): string {
  return css(name)
}

/**
 * A SPEECH BUBBLE, AND NOBODY IN IT — "somebody is talking to you".
 *
 * The bubble has SHAPES in it, never letters: a pre-reader reads three dots as
 * "they are saying something", and reads letters as "this is not for me".
 *
 * ===========================================================================
 *  WHY THE LITTLE TEAL MONSTER WENT (fixed vr1 — MAJOR)
 * ===========================================================================
 * This drawing used to have its own generic monster head beside the bubble, and
 * the errand card draws it inside the `talk` step chip — directly underneath the
 * card's big portrait of the monster who actually asked. Critic: *"the giver's
 * portrait is a green capsule … meanwhile the ACTIVE step chip, on the same
 * card, contains a completely DIFFERENT monster head (dark teal, two round
 * eyes). Two faces for one person on one card."* To a three-year-old who cannot
 * read either name, that is not an inconsistency, it is two monsters — the exact
 * bug `src/npc/town-life.ts` fixed for the card portrait, reappearing one level
 * down inside the step icon.
 *
 * So the person is gone from the picture, and `src/ui/errands.ts` puts the REAL
 * giver's portrait in the chip with this bubble as a corner badge. The bubble is
 * now bigger and centred, which also makes it legible as the 18 px badge it has
 * to be.
 */
const talk = `
  <path d="M14 12 L86 12 C92 12 96 16 96 23 L96 60 C96 67 92 71 86 71 L52 71 L30 90 L34 71 L14 71
           C8 71 4 67 4 60 L4 23 C4 16 8 12 14 12 Z"
        fill="${f('uiPanel')}" ${OUT}/>
  <circle cx="28" cy="42" r="7.5" fill="${f('uiAccent')}"/>
  <circle cx="50" cy="42" r="7.5" fill="${f('uiPrimary')}"/>
  <circle cx="72" cy="42" r="7.5" fill="${f('uiCoin')}"/>`

/** AN APPLE AND A BERRY — "get some fruit". Round, red, with a leaf. */
const fruit = `
  <path d="M50 30 C62 20 82 24 84 46 C86 68 68 88 50 88 C32 88 14 68 16 46 C18 24 38 20 50 30 Z"
        fill="${f('flowerRed')}" ${OUT}/>
  <path d="M50 30 L50 12" ${OUT} fill="none" stroke="${f('trunkDark')}"/>
  <path d="M50 30 L50 12" ${DET} fill="none"/>
  <path d="M52 18 C64 6 82 8 84 16 C86 26 66 30 52 18 Z" fill="${f('leaf')}" ${DET}/>
  <ellipse cx="34" cy="50" rx="7" ry="9" fill="${f('flowerWhite')}" opacity="0.55"/>`

/** A BOWL OF FRUIT — "cook something". The dish, not the pot. */
const bowl = `
  <circle cx="36" cy="42" r="13" fill="${f('flowerRed')}" ${DET}/>
  <circle cx="60" cy="38" r="12" fill="${f('monsterSunny')}" ${DET}/>
  <circle cx="50" cy="52" r="11" fill="${f('leaf')}" ${DET}/>
  <path d="M10 54 L90 54 C90 78 74 90 50 90 C26 90 10 78 10 54 Z" fill="${f('uiPanel')}" ${OUT}/>
  <path d="M20 64 C30 72 44 76 50 76" ${DET} fill="none" opacity="0.5"/>`

/** A ZOO ANIMAL BEHIND A GATE — "the animals". A face, ears, and two bars. */
const zoo = `
  <circle cx="26" cy="30" r="11" fill="${f('monsterCoral')}" ${OUT}/>
  <circle cx="74" cy="30" r="11" fill="${f('monsterCoral')}" ${OUT}/>
  <path d="M50 16 C74 16 88 34 88 54 C88 74 72 88 50 88 C28 88 12 74 12 54 C12 34 26 16 50 16 Z"
        fill="${f('monsterCoral')}" ${OUT}/>
  <circle cx="36" cy="50" r="8" fill="${f('eyeWhite')}" ${DET}/>
  <circle cx="64" cy="50" r="8" fill="${f('eyeWhite')}" ${DET}/>
  <circle cx="37" cy="52" r="3.5" fill="${f('pupil')}"/>
  <circle cx="65" cy="52" r="3.5" fill="${f('pupil')}"/>
  <ellipse cx="50" cy="68" rx="9" ry="7" fill="${f('monsterCream')}" ${DET}/>
  <path d="M50 68 L50 76" ${DET} fill="none"/>`

/** A PARK BENCH — the neighbour-request headline: "a bench by the pond". */
const bench = `
  <path d="M14 44 L86 44 M14 58 L86 58" stroke="${f('wood')}" stroke-width="13"
        stroke-linecap="round" fill="none"/>
  <path d="M14 44 L86 44 M14 58 L86 58" ${DET} fill="none"/>
  <path d="M22 34 L22 86 M78 34 L78 86" ${OUT} fill="none"/>
  <path d="M14 72 L86 72" stroke="${f('woodLight')}" stroke-width="13" stroke-linecap="round" fill="none"/>
  <path d="M14 72 L86 72" ${DET} fill="none"/>`

/** A NOTE IN THE POST — how a morning errand arrives. Squiggles, never words. */
const note = `
  <path d="M12 24 L88 24 C92 24 94 27 94 31 L94 77 C94 81 92 84 88 84 L12 84
           C8 84 6 81 6 77 L6 31 C6 27 8 24 12 24 Z" fill="${f('uiPanel')}" ${OUT}/>
  <path d="M8 28 L50 58 L92 28" ${OUT} fill="none"/>
  <path d="M26 66 L58 66 M26 76 L44 76" ${DET} fill="none" opacity="0.45"/>`

/** THE GUIDE ARROW ITSELF — a fat chevron with a tail. Points UP by default. */
const guide = `
  <path d="M50 8 L88 50 L66 50 L66 90 L34 90 L34 50 L12 50 Z"
        fill="${f('uiPrimary')}" ${OUT}/>
  <path d="M50 20 L74 46 L58 46" fill="${f('uiCoin')}" opacity="0.55"/>`

const EXTRA: Readonly<Record<string, string>> = {
  talk,
  fruit,
  bowl,
  zoo,
  bench,
  note,
  guide,
}

/** Is this a picture SOMETHING in the game can draw? */
export function hasErrandIcon(name: string): boolean {
  return hasIcon(name) || EXTRA[name] !== undefined
}

/**
 * The HUD's set first, this crew's second. A name that is in neither still
 * returns a friendly blob from `iconSvg` — a blank square reads to a kid as
 * "broken", a blob reads as "a thing".
 */
export function errandIconSvg(name: string): string {
  const extra = EXTRA[name]
  if (!hasIcon(name) && extra !== undefined) {
    return `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" focusable="false" aria-hidden="true">${extra}</svg>`
  }
  return iconSvg(name)
}

/**
 * A picture as a DOM element, sized in CSS px.
 *
 * SIZED INLINE, exactly like `iconEl()`, and for the reason written up at
 * length there: a viewBox-only `<svg>` inflates to its containing block the
 * moment `.mt-ico` has not landed, which is how one coin ends up 830 px across.
 */
export function errandIcon(name: string, sizePx: number): HTMLSpanElement {
  if (hasIcon(name)) return iconEl(name, sizePx)
  const span = document.createElement('span')
  span.className = 'mt-ico'
  span.style.display = 'block'
  span.style.flex = 'none'
  span.style.width = `${sizePx}px`
  span.style.height = `${sizePx}px`
  span.innerHTML = errandIconSvg(name)
  const svg = span.firstElementChild
  if (svg !== null) {
    svg.setAttribute('width', '100%')
    svg.setAttribute('height', '100%')
  }
  return span
}

/** Test surface: the keys this crew adds on top of the HUD set. */
export function errandIconNames(): string[] {
  return Object.keys(EXTRA).sort()
}
